import type { FieldDef, ProjectFieldRef, SchemaIndex } from "@gjwtrack/query-language";
import { useEffect, useState } from "react";
import { BUNDLE_KINDS, fieldValueText } from "../lib/format.ts";
import { Avatar, ValueBadge } from "./Badges.tsx";
import { Input, InputSize } from "../ui/ring.ts";
import { ValueSelect, type ValueOption } from "../ui/ValueSelect.tsx";

type Values = ReadonlyArray<number | string>;

export interface FieldEditorProps {
  schema: SchemaIndex;
  def: FieldDef;
  pf: ProjectFieldRef | undefined;
  values: Values;
  onChange: (values: Values) => void;
  disabled?: boolean;
}

/** Option-set and user fields pick from a list; everything else is typed. */
export function isPickerKind(def: FieldDef): boolean {
  return BUNDLE_KINDS.has(def.kind) || def.kind === "user";
}

/** Picker rows: bundle elements in ordinal order with their colour badge, users by name with an avatar. */
export function pickerOptions(schema: SchemaIndex, def: FieldDef, pf?: ProjectFieldRef): ValueOption[] {
  if (def.kind === "user") {
    // A project's user field may carry a value set (YouTrack's user bundle): only those users are offered.
    const allowed = pf?.userIds ? new Set(pf.userIds) : null;
    return [...schema.data.users]
      .filter((u) => allowed === null || allowed.has(u.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((u) => ({ id: u.id, label: u.name, icon: <Avatar schema={schema} userId={u.id} size={20} /> }));
  }
  const els = def.bundleId === undefined ? [] : schema.elementsByBundle.get(def.bundleId) ?? [];
  return els.map((e) => ({ id: e.id, label: e.name, icon: <ValueBadge schema={schema} def={def} value={e.id} /> }));
}

/** One editor per field kind. Pickers commit per choice, typed values on blur / Enter. */
export function FieldEditor({ schema, def, pf, values, onChange, disabled }: FieldEditorProps) {
  if (isPickerKind(def)) return <PickerField schema={schema} def={def} pf={pf} values={values} onChange={onChange} disabled={disabled ?? false} />;
  if (def.kind === "group") {
    return <span className="text-muted">{values.map((v) => fieldValueText(schema, def, v)).join(", ") || "—"}</span>;
  }
  if (def.kind === "date" || def.kind === "datetime") {
    const v = values[0];
    const iso = typeof v === "number" ? (def.kind === "date" ? new Date(v).toISOString().slice(0, 10) : toLocalDateTime(v)) : "";
    return (
      <Input
        size={InputSize.FULL}
        type={def.kind === "date" ? "date" : "datetime-local"}
        disabled={disabled ?? false}
        value={iso}
        onChange={(e) => {
          if (e.target.value === "") return onChange([]);
          const ms = def.kind === "date" ? Date.parse(`${e.target.value}T00:00:00Z`) : new Date(e.target.value).getTime();
          if (!Number.isNaN(ms)) onChange([ms]);
        }}
      />
    );
  }
  // integer / float / period / string / text: typed, committed on blur or Enter.
  return <TypedEditor def={def} values={values} onChange={onChange} disabled={disabled ?? false} />;
}

/** Select-shaped Ring picker for forms (admin defaults, typed contexts); the issue panel and New issue use the inline variant. */
function PickerField({ schema, def, pf, values, onChange, disabled }: { schema: SchemaIndex; def: FieldDef; pf: ProjectFieldRef | undefined; values: Values; onChange: (v: Values) => void; disabled: boolean }) {
  const required = pf?.required ?? false;
  return (
    <ValueSelect
      options={pickerOptions(schema, def, pf)}
      selected={new Set(values.filter((v): v is number => typeof v === "number"))}
      multi={def.multi}
      clearable={!required}
      emptyLabel={required ? "Required" : "—"}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function TypedEditor({ def, values, onChange, disabled }: { def: FieldDef; values: Values; onChange: (v: Values) => void; disabled: boolean }) {
  const initial = values[0] === undefined ? "" : String(values[0]);
  const [draft, setDraft] = useState(initial);
  useEffect(() => setDraft(initial), [initial]);
  const commit = (): void => {
    if (draft === initial) return;
    if (draft.trim() === "") return onChange([]);
    if (def.kind === "string" || def.kind === "text") return onChange([draft]);
    const n = def.kind === "float" ? Number(draft) : Math.trunc(Number(draft));
    if (Number.isFinite(n)) onChange([n]);
    else setDraft(initial);
  };
  if (def.kind === "text") {
    return (
      <Input multiline size={InputSize.FULL} rows={3} disabled={disabled} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
    );
  }
  return (
    <Input
      size={InputSize.FULL}
      type={def.kind === "string" ? "text" : "number"}
      step={def.kind === "float" ? "any" : 1}
      placeholder={def.kind === "period" ? "minutes" : undefined}
      disabled={disabled}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setDraft(initial);
      }}
    />
  );
}

function toLocalDateTime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
